// client/src/coms/views/canvas_parts/canvas_room_view_parts/liquifyUtils.js
import { clipContextToSelection } from './selectionUtils';

export const DEFAULT_DISTORTION_MODE = 'flow_shift';
export const DEFAULT_LIQUIFY_MODE = DEFAULT_DISTORTION_MODE;

export const DEFAULT_LIQUIFY_SETTINGS = {
  force: 0.5,
  flow: 0.5,
  twist: 0.5,
  chaos: 0.5,
  detail: 0.5,
  edge: 0.5,
};

export const DISTORTION_MODES = [
  { id: 'smudge', label: 'Smudge', shortLabel: 'Smudge', description: 'Blend and drag wet pixels without keeping Smudge as a separate tool.' },
  { id: 'flow_shift', label: 'Flow Shift', shortLabel: 'Flow', description: 'Drag the image like wet paint caught in a current.' },
  { id: 'field_paint', label: 'Flow Field Painting', shortLabel: 'Field', description: 'Paint persistent direction vectors, then apply them so the whole surface flows through the painted current.' },
  { id: 'clock_well', label: 'Clockwell', shortLabel: 'Clock', description: 'Fold pixels around the cursor in a clockwise local field.' },
  { id: 'counter_well', label: 'Counterwell', shortLabel: 'Counter', description: 'Fold pixels around the cursor in a counter-clockwise local field.' },
  { id: 'collapse', label: 'Collapse', shortLabel: 'Collapse', description: 'Draw pixels inward as if the surface is sinking.' },
  { id: 'bloom', label: 'Bloom', shortLabel: 'Bloom', description: 'Open pixels outward from the centre of the brush.' },
  { id: 'shardfield', label: 'Shardfield', shortLabel: 'Shard', description: 'Fractured angular movement driven by layered fractal noise.' },
  { id: 'veinflow', label: 'Vein', shortLabel: 'Vein', description: 'Branching organic distortion, like roots, lightning, or blood vessels.' },
  { id: 'faultline', label: 'Faultline', shortLabel: 'Fault', description: 'Shear pixels across broken tectonic fracture lines.' },
  { id: 'gravity_well', label: 'Gravity Well', shortLabel: 'Gravity', description: 'Bend pixels into a local mass field around the cursor.' },
  { id: 'waveglass', label: 'Waveglass', shortLabel: 'Wave', description: 'Warp through concentric glass-like wave distortion.' },
  { id: 'water_reflection', label: 'Water Reflection', shortLabel: 'Water', description: 'Split colour into perspective water bands with calm distance, heavy lower smears, and broken horizontal ripple drift.' },
  { id: 'rim_shear', label: 'Rim Shear', shortLabel: 'Rim', description: 'Move the brush rim harder than the centre for edge-driven distortion.' },
  { id: 'restore', label: 'Restore Field', shortLabel: 'Restore', description: 'Brush pixels back toward the stroke start snapshot.' },
  { id: 'soft_nudge', label: 'Soft Nudge', shortLabel: 'Nudge', description: 'A gentle broad field for correcting an existing warp.' },
];

export const LIQUIFY_MODES = DISTORTION_MODES;

const DISTORTION_MODE_ALIASES = {
  smudge_tool: 'smudge',
  smear: 'smudge',
  blend: 'smudge',
  push: 'flow_shift',
  flow_field: 'field_paint',
  field: 'field_paint',
  twirl_right: 'clock_well',
  twirl_left: 'counter_well',
  pinch: 'collapse',
  expand: 'bloom',
  crystals: 'shardfield',
  veins: 'veinflow',
  fault: 'faultline',
  gravity: 'gravity_well',
  ripple: 'waveglass',
  water: 'water_reflection',
  reflection: 'water_reflection',
  reflect: 'water_reflection',
  edge: 'rim_shear',
  reconstruct: 'restore',
  adjust: 'soft_nudge',
};

const FLOW_FIELD_CELL_SIZE = 12;
const FLOW_FIELD_DECAY = 0.985;
const FLOW_FIELD_MAX = 42;
const FLOW_FIELD_EPSILON = 0.001;
const FLOW_FIELD_APPLY_PAD = FLOW_FIELD_CELL_SIZE + FLOW_FIELD_MAX + 8;
const WATER_REFLECTION_PIXEL_STEP = 2;
const WATER_REFLECTION_MAX_RADIUS = 220;
const flowFieldStore = new WeakMap();

export function normaliseDistortionMode(value) {
  const candidate = typeof value === 'string' ? value : DEFAULT_DISTORTION_MODE;
  const aliased = DISTORTION_MODE_ALIASES[candidate] || candidate;
  return DISTORTION_MODES.some((mode) => mode.id === aliased) ? aliased : DEFAULT_DISTORTION_MODE;
}

export function getDistortionModeInfo(value) {
  const safeMode = normaliseDistortionMode(value);
  return DISTORTION_MODES.find((mode) => mode.id === safeMode) || DISTORTION_MODES[0];
}

export function normaliseLiquifyMode(value) {
  return normaliseDistortionMode(value);
}

export function getLiquifyModeInfo(value) {
  return getDistortionModeInfo(value);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function clampInt(value, min, max) {
  return Math.max(min, Math.min(max, value | 0));
}

function clampUnit(value, fallback = 0.5) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  if (number <= 0) return 0;
  if (number >= 1) return 1;
  return number;
}

export function normaliseLiquifySettings(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    force: clampUnit(source.force, DEFAULT_LIQUIFY_SETTINGS.force),
    flow: clampUnit(source.flow, DEFAULT_LIQUIFY_SETTINGS.flow),
    twist: clampUnit(source.twist, DEFAULT_LIQUIFY_SETTINGS.twist),
    chaos: clampUnit(source.chaos, DEFAULT_LIQUIFY_SETTINGS.chaos),
    detail: clampUnit(source.detail, DEFAULT_LIQUIFY_SETTINGS.detail),
    edge: clampUnit(source.edge, DEFAULT_LIQUIFY_SETTINGS.edge),
  };
}

function getLiquifyForceScale(settings) {
  return 0.25 + (clampUnit(settings?.force, DEFAULT_LIQUIFY_SETTINGS.force) * 1.5);
}

function getLiquifyFlowScale(settings) {
  return 0.25 + (clampUnit(settings?.flow, DEFAULT_LIQUIFY_SETTINGS.flow) * 1.5);
}

function getLiquifyTwistScale(settings) {
  return 0.25 + (clampUnit(settings?.twist, DEFAULT_LIQUIFY_SETTINGS.twist) * 1.5);
}

function getLiquifyChaosScale(settings) {
  return clampUnit(settings?.chaos, DEFAULT_LIQUIFY_SETTINGS.chaos) * 2;
}

function getLiquifyDetailScale(settings) {
  return 0.5 + clampUnit(settings?.detail, DEFAULT_LIQUIFY_SETTINGS.detail);
}

function shapeLiquifyFalloff(falloff, settings) {
  const edge = clampUnit(settings?.edge, DEFAULT_LIQUIFY_SETTINGS.edge);
  const curve = 1.8 - (edge * 1.6);
  return Math.pow(clamp(falloff, 0, 1), Math.max(0.18, curve));
}

function normaliseSeed(value) {
  const seed = Number(value);
  if (!Number.isFinite(seed)) return 0;
  return seed;
}

function smoothFalloff(t) {
  const safe = clamp(t, 0, 1);
  return safe * safe * (3 - (2 * safe));
}

function hash2(x, y, seed = 0) {
  const n = Math.sin((x * 127.1) + (y * 311.7) + (seed * 74.7)) * 43758.5453123;
  return n - Math.floor(n);
}

function valueNoise(x, y, seed = 0) {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - (2 * fx));
  const uy = fy * fy * (3 - (2 * fy));
  const a = hash2(ix, iy, seed);
  const b = hash2(ix + 1, iy, seed);
  const c = hash2(ix, iy + 1, seed);
  const d = hash2(ix + 1, iy + 1, seed);
  const ab = a * (1 - ux) + b * ux;
  const cd = c * (1 - ux) + d * ux;
  return ab * (1 - uy) + cd * uy;
}

function fbm(x, y, seed = 0, octaves = 3) {
  let value = 0;
  let amp = 0.5;
  let freq = 1;
  let total = 0;

  for (let i = 0; i < octaves; i += 1) {
    value += valueNoise(x * freq, y * freq, seed + i * 17.31) * amp;
    total += amp;
    amp *= 0.5;
    freq *= 2.03;
  }

  return total > 0 ? value / total : 0;
}

function fastFbm2(x, y, seed = 0) {
  return (
    valueNoise(x, y, seed) * 0.58
    + valueNoise(x * 2.03 + 19.7, y * 2.03 - 11.3, seed + 17.31) * 0.29
    + valueNoise(x * 4.11 - 37.1, y * 4.11 + 23.9, seed + 29.77) * 0.13
  );
}

function sampleBilinearInto(source, width, height, sampleX, sampleY, result, dstIndex) {
  const x0 = clampInt(Math.floor(sampleX), 0, width - 1);
  const y0 = clampInt(Math.floor(sampleY), 0, height - 1);
  const x1 = x0 < width - 1 ? x0 + 1 : x0;
  const y1 = y0 < height - 1 ? y0 + 1 : y0;
  const tx = clamp(sampleX - x0, 0, 1);
  const ty = clamp(sampleY - y0, 0, 1);
  const i00 = (y0 * width + x0) * 4;
  const i10 = (y0 * width + x1) * 4;
  const i01 = (y1 * width + x0) * 4;
  const i11 = (y1 * width + x1) * 4;
  const ux = 1 - tx;
  const uy = 1 - ty;

  const w00 = ux * uy;
  const w10 = tx * uy;
  const w01 = ux * ty;
  const w11 = tx * ty;

  result[dstIndex] = (source[i00] * w00) + (source[i10] * w10) + (source[i01] * w01) + (source[i11] * w11);
  result[dstIndex + 1] = (source[i00 + 1] * w00) + (source[i10 + 1] * w10) + (source[i01 + 1] * w01) + (source[i11 + 1] * w11);
  result[dstIndex + 2] = (source[i00 + 2] * w00) + (source[i10 + 2] * w10) + (source[i01 + 2] * w01) + (source[i11 + 2] * w11);
  result[dstIndex + 3] = (source[i00 + 3] * w00) + (source[i10 + 3] * w10) + (source[i01 + 3] * w01) + (source[i11 + 3] * w11);
}

function copyImageData(imageData) {
  return new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
}

function getWaterPerspective(worldY, canvasHeight) {
  const canvasDepth = Math.max(1, canvasHeight || 1);
  const horizon = 0.25;
  const depthT = clamp(worldY / canvasDepth, 0, 1);
  const perspectiveT = clamp((depthT - horizon) / Math.max(0.001, 1 - horizon), 0, 1);

  return {
    t: perspectiveT,
    horizontal: Math.pow(perspectiveT, 2.2),
    vertical: Math.pow(perspectiveT, 2.85),
  };
}

function writeWaterReflectionPixel(source, result, width, height, x, y, sampleX, sampleY, offsetX, perspective) {
  const smearSamples = perspective > 0.64 ? 3 : perspective > 0.28 ? 2 : 1;
  const dstIndex = (y * width + x) * 4;

  if (smearSamples <= 1) {
    sampleBilinearInto(source, width, height, sampleX, sampleY, result, dstIndex);
    return;
  }

  const smearSign = Math.sign(offsetX || 1);
  const smearStep = 0.7 + (perspective * 1.75);
  let r = 0;
  let g = 0;
  let b = 0;
  let a = 0;
  let totalWeight = 0;

  for (let i = 0; i < smearSamples; i += 1) {
    const weight = i === 0 ? 1 : i === 1 ? 0.5 : 0.26;
    const sx = clamp(sampleX - (smearSign * i * smearStep), 0, width - 1);
    const sy = clamp(sampleY, 0, height - 1);
    const x0 = clampInt(Math.floor(sx), 0, width - 1);
    const y0 = clampInt(Math.floor(sy), 0, height - 1);
    const x1 = x0 < width - 1 ? x0 + 1 : x0;
    const y1 = y0 < height - 1 ? y0 + 1 : y0;
    const tx = clamp(sx - x0, 0, 1);
    const ty = clamp(sy - y0, 0, 1);
    const i00 = (y0 * width + x0) * 4;
    const i10 = (y0 * width + x1) * 4;
    const i01 = (y1 * width + x0) * 4;
    const i11 = (y1 * width + x1) * 4;
    const ux = 1 - tx;
    const uy = 1 - ty;
    const w00 = ux * uy * weight;
    const w10 = tx * uy * weight;
    const w01 = ux * ty * weight;
    const w11 = tx * ty * weight;

    r += (source[i00] * w00) + (source[i10] * w10) + (source[i01] * w01) + (source[i11] * w11);
    g += (source[i00 + 1] * w00) + (source[i10 + 1] * w10) + (source[i01 + 1] * w01) + (source[i11 + 1] * w11);
    b += (source[i00 + 2] * w00) + (source[i10 + 2] * w10) + (source[i01 + 2] * w01) + (source[i11 + 2] * w11);
    a += (source[i00 + 3] * w00) + (source[i10 + 3] * w10) + (source[i01 + 3] * w01) + (source[i11 + 3] * w11);
    totalWeight += weight;
  }

  result[dstIndex] = r / totalWeight;
  result[dstIndex + 1] = g / totalWeight;
  result[dstIndex + 2] = b / totalWeight;
  result[dstIndex + 3] = a / totalWeight;
}

function getSelectionTempContext(width, height, selection, offsetX = 0, offsetY = 0) {
  if (!selection) return null;

  const temp = document.createElement('canvas');
  temp.width = width;
  temp.height = height;

  const ctx = temp.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.save();
  ctx.translate(-offsetX, -offsetY);
  clipContextToSelection(ctx, selection);
  ctx.restore();

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  return ctx;
}

function getSelectionMaskData(width, height, selection, offsetX = 0, offsetY = 0) {
  if (!selection) return null;
  const maskCtx = getSelectionTempContext(width, height, selection, offsetX, offsetY);
  return maskCtx ? maskCtx.getImageData(0, 0, width, height).data : null;
}

function selectionContains(maskData, maskWidth, x, y) {
  if (!maskData) return true;
  if (x < 0 || y < 0 || x >= maskWidth) return false;
  return maskData[(y * maskWidth + x) * 4 + 3] > 0;
}

function getFlowField(targetCanvas) {
  let field = flowFieldStore.get(targetCanvas);
  const width = Math.max(1, Math.ceil(targetCanvas.width / FLOW_FIELD_CELL_SIZE));
  const height = Math.max(1, Math.ceil(targetCanvas.height / FLOW_FIELD_CELL_SIZE));
  const length = width * height * 2;

  if (!field || field.width !== width || field.height !== height || field.values.length !== length) {
    field = {
      width,
      height,
      values: new Float32Array(length),
      dirty: false,
    };
    flowFieldStore.set(targetCanvas, field);
  }

  return field;
}

function addToFlowField(field, canvasX, canvasY, vx, vy, falloff, strength) {
  const gx = Math.floor(canvasX / FLOW_FIELD_CELL_SIZE);
  const gy = Math.floor(canvasY / FLOW_FIELD_CELL_SIZE);

  if (gx < 0 || gy < 0 || gx >= field.width || gy >= field.height) {
    return;
  }

  const index = (gy * field.width + gx) * 2;
  const add = falloff * strength * 0.72;
  const nextX = clamp((field.values[index] * FLOW_FIELD_DECAY) + (vx * add), -FLOW_FIELD_MAX, FLOW_FIELD_MAX);
  const nextY = clamp((field.values[index + 1] * FLOW_FIELD_DECAY) + (vy * add), -FLOW_FIELD_MAX, FLOW_FIELD_MAX);

  field.values[index] = nextX;
  field.values[index + 1] = nextY;
  field.dirty = true;
}

function sampleFlowField(field, canvasX, canvasY) {
  const fx = canvasX / FLOW_FIELD_CELL_SIZE;
  const fy = canvasY / FLOW_FIELD_CELL_SIZE;
  const x0 = clampInt(Math.floor(fx), 0, field.width - 1);
  const y0 = clampInt(Math.floor(fy), 0, field.height - 1);
  const x1 = x0 < field.width - 1 ? x0 + 1 : x0;
  const y1 = y0 < field.height - 1 ? y0 + 1 : y0;
  const tx = clamp(fx - x0, 0, 1);
  const ty = clamp(fy - y0, 0, 1);
  const i00 = (y0 * field.width + x0) * 2;
  const i10 = (y0 * field.width + x1) * 2;
  const i01 = (y1 * field.width + x0) * 2;
  const i11 = (y1 * field.width + x1) * 2;
  const ax = field.values[i00] * (1 - tx) + field.values[i10] * tx;
  const ay = field.values[i00 + 1] * (1 - tx) + field.values[i10 + 1] * tx;
  const bx = field.values[i01] * (1 - tx) + field.values[i11] * tx;
  const by = field.values[i01 + 1] * (1 - tx) + field.values[i11 + 1] * tx;

  return {
    x: ax * (1 - ty) + bx * ty,
    y: ay * (1 - ty) + by * ty,
  };
}

function getFlowFieldPixelBounds(field, canvasWidth, canvasHeight, strength) {
  let minCellX = field.width;
  let minCellY = field.height;
  let maxCellX = -1;
  let maxCellY = -1;

  for (let y = 0; y < field.height; y += 1) {
    for (let x = 0; x < field.width; x += 1) {
      const index = (y * field.width + x) * 2;
      if (Math.abs(field.values[index]) > FLOW_FIELD_EPSILON || Math.abs(field.values[index + 1]) > FLOW_FIELD_EPSILON) {
        minCellX = Math.min(minCellX, x);
        minCellY = Math.min(minCellY, y);
        maxCellX = Math.max(maxCellX, x);
        maxCellY = Math.max(maxCellY, y);
      }
    }
  }

  if (maxCellX < minCellX || maxCellY < minCellY) return null;

  const pad = Math.ceil(FLOW_FIELD_APPLY_PAD * Math.max(1, strength));
  return {
    startX: clampInt((minCellX * FLOW_FIELD_CELL_SIZE) - pad, 0, canvasWidth),
    startY: clampInt((minCellY * FLOW_FIELD_CELL_SIZE) - pad, 0, canvasHeight),
    endX: clampInt(((maxCellX + 1) * FLOW_FIELD_CELL_SIZE) + pad, 0, canvasWidth),
    endY: clampInt(((maxCellY + 1) * FLOW_FIELD_CELL_SIZE) + pad, 0, canvasHeight),
  };
}

export function clearDistortionFlowField(targetCanvas) {
  const field = flowFieldStore.get(targetCanvas);
  if (field) {
    field.values.fill(0);
    field.dirty = false;
  }
}

export function clearLiquifyFlowField(targetCanvas) {
  clearDistortionFlowField(targetCanvas);
}

export function applyDistortionFlowFieldWithinSelection(targetCanvas, options = {}, selection = null) {
  if (!targetCanvas) return false;

  const ctx = targetCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  const field = getFlowField(targetCanvas);
  if (!field || !field.values || field.values.length <= 0) return false;

  const canvasWidth = targetCanvas.width;
  const canvasHeight = targetCanvas.height;
  const strength = Math.max(0.02, Math.min(3, Number(options?.strength ?? options?.opacity ?? 1)));
  const iterations = Math.max(1, Math.min(4, Math.round(Number(options?.iterations) || 1)));
  const bounds = getFlowFieldPixelBounds(field, canvasWidth, canvasHeight, strength);

  if (!bounds) return false;

  const startX = bounds.startX;
  const startY = bounds.startY;
  const width = Math.max(1, bounds.endX - bounds.startX);
  const height = Math.max(1, bounds.endY - bounds.startY);

  let maskData = null;
  if (selection) {
    maskData = getSelectionMaskData(width, height, selection, startX, startY);
  }

  let changed = false;

  for (let pass = 0; pass < iterations; pass += 1) {
    const sourceData = ctx.getImageData(startX, startY, width, height);
    const resultData = copyImageData(sourceData);
    const source = sourceData.data;
    const result = resultData.data;

    for (let y = 0; y < height; y += 1) {
      const canvasY = startY + y;

      for (let x = 0; x < width; x += 1) {
        if (!selectionContains(maskData, width, x, y)) {
          continue;
        }

        const canvasX = startX + x;
        const flow = sampleFlowField(field, canvasX, canvasY);

        if ((flow.x * flow.x) + (flow.y * flow.y) <= FLOW_FIELD_EPSILON) {
          continue;
        }

        const sampleX = x - (flow.x * strength);
        const sampleY = y - (flow.y * strength);

        if (sampleX < 0 || sampleY < 0 || sampleX >= width || sampleY >= height) {
          continue;
        }

        sampleBilinearInto(source, width, height, sampleX, sampleY, result, (y * width + x) * 4);
        changed = true;
      }
    }

    ctx.putImageData(resultData, startX, startY);
  }

  return changed;
}

export function applyLiquifyFlowFieldWithinSelection(targetCanvas, options = {}, selection = null) {
  return applyDistortionFlowFieldWithinSelection(targetCanvas, options, selection);
}

function getWaterRowCache(startY, height, canvasHeight, seed) {
  const rows = new Array(height);

  for (let y = 0; y < height; y += 1) {
    const worldY = startY + y;
    const perspective = getWaterPerspective(worldY, canvasHeight);

    if (perspective.horizontal <= 0.0001) {
      rows[y] = {
        perspective,
        stretch: 1,
        broadBand: 0,
        brokenBand: 0,
        longWave: 0,
        verticalBase: 0,
        lowerSmearBoost: 0,
      };
      continue;
    }

    const stretch = 1 + (perspective.horizontal * 2.5);
    const broadBand = fastFbm2(worldY * (0.018 / stretch), seed * 0.011, seed + 311);
    const brokenBand = fastFbm2(worldY * (0.045 / stretch), seed * 0.017, seed + 917);
    const longWave = Math.sin((worldY * 0.055 / stretch) + (seed * 0.133) + ((brokenBand - 0.5) * 2.8));
    const verticalBase = fastFbm2(worldY * 0.026, seed * 0.019, seed + 641) - 0.5;

    rows[y] = {
      perspective,
      stretch,
      broadBand,
      brokenBand,
      longWave,
      verticalBase,
      lowerSmearBoost: 0.65 + (perspective.horizontal * 1.55),
    };
  }

  return rows;
}

function getSmoothWaterOffset(row, worldX, localX, localY, dx, dy, radius, strength, distance, seed, dragDistance = null) {
  const perspective = row.perspective;

  if (perspective.horizontal <= 0.0001) {
    return { x: 0, y: 0, perspective: 0 };
  }

  const nx = radius > 0 ? localX / radius : 0;
  const ny = radius > 0 ? localY / radius : 0;
  const falloff = smoothFalloff(1 - (distance / Math.max(1, radius)));
  const power = strength * falloff;
  const safeDragDistance = dragDistance == null ? Math.hypot(dx, dy) : dragDistance;
  const horizontalSign = Math.sign(dx || 1);

  const fineScale = 0.035 + (perspective.horizontal * 0.03);
  const fineNoise = fastFbm2(worldX * fineScale, row.brokenBand * 8.5, seed + 733);
  const crossNoise = fastFbm2(worldX * 0.012, row.broadBand * 5.5, seed + 421);
  const bandBlend = clamp(row.broadBand + ((row.brokenBand - 0.5) * 0.38) + ((crossNoise - 0.5) * 0.22), 0, 1);
  const bandDirection = Math.sin((bandBlend * Math.PI * 2) + (row.longWave * 0.65)) >= 0 ? 1 : -1;

  const tightWave = Math.sin(
    (worldX * (0.018 + perspective.horizontal * 0.012))
    + (row.stretch * 1.7)
    + (seed * 0.071)
    + ((fineNoise - 0.5) * 2.2),
  );

  const bandAmount = safeDragDistance
    * power
    * bandDirection
    * (0.18 + Math.abs((bandBlend - 0.5) * 2) * 0.72)
    * row.lowerSmearBoost
    * perspective.horizontal;

  const rippleAmount = radius
    * power
    * (0.018 + perspective.horizontal * 0.038)
    * (tightWave + (row.longWave * 0.38) + ((fineNoise - 0.5) * 0.82))
    * perspective.horizontal;

  const verticalWobble = radius
    * power
    * 0.01
    * (((fineNoise - 0.5) * 1.35) + (row.longWave * 0.32) + (row.verticalBase * 0.58))
    * perspective.vertical;

  const edgeBreak = smoothFalloff(distance / Math.max(1, radius))
    * radius
    * power
    * 0.008
    * ((crossNoise - 0.5) + (fineNoise - 0.5) * 0.45)
    * perspective.horizontal;

  return {
    x: (horizontalSign * (bandAmount + rippleAmount)) + (nx * edgeBreak),
    y: verticalWobble + (ny * edgeBreak * 0.35),
    perspective: perspective.horizontal,
  };
}

function writeWaterReflectionPixelSpan(source, result, width, height, x, y, sampleX, sampleY, offsetX, perspective, span) {
  for (let i = 0; i < span; i += 1) {
    const px = x + i;
    if (px >= width) break;
    writeWaterReflectionPixel(source, result, width, height, px, y, sampleX + i, sampleY, offsetX, perspective);
  }
}

function drawWaterReflectionStroke(source, result, width, height, startX, startY, centreX, centreY, dx, dy, radius, strength, seed, canvasHeight, maskData) {
  const waterRows = getWaterRowCache(startY, height, canvasHeight, seed);
  const radiusSq = radius * radius;
  const dragDistance = Math.hypot(dx, dy);
  let changed = false;

  for (let y = 0; y < height; y += 1) {
    const canvasY = startY + y;
    const localY = canvasY - centreY;
    const rowRadiusSq = radiusSq - (localY * localY);

    if (rowRadiusSq < 0) continue;

    const row = waterRows[y];
    if (!row || row.perspective.horizontal <= 0.0001) continue;

    const rowRadius = Math.sqrt(rowRadiusSq);
    const minX = Math.max(0, Math.floor((centreX - rowRadius) - startX));
    const maxX = Math.min(width - 1, Math.ceil((centreX + rowRadius) - startX));

    for (let x = minX; x <= maxX; x += WATER_REFLECTION_PIXEL_STEP) {
      if (!selectionContains(maskData, width, x, y)) continue;

      const canvasX = startX + x;
      const localX = canvasX - centreX;
      const distance = Math.sqrt((localX * localX) + (localY * localY));
      const offset = getSmoothWaterOffset(
        row,
        canvasX,
        localX,
        localY,
        dx,
        dy,
        radius,
        strength,
        distance,
        seed,
        dragDistance,
      );

      const sampleX = x - offset.x;
      const sampleY = y - offset.y;

      if (sampleX < 0 || sampleY < 0 || sampleX >= width || sampleY >= height) {
        continue;
      }

      const remaining = maxX - x + 1;
      const span = Math.min(WATER_REFLECTION_PIXEL_STEP, remaining);
      writeWaterReflectionPixelSpan(source, result, width, height, x, y, sampleX, sampleY, offset.x, offset.perspective || 0, span);
      changed = true;
    }
  }

  return changed;
}

function getSourceOffsetForMode(mode, localX, localY, dx, dy, radius, strength, distance, centreX, centreY, flowField = null, seed = 0, canvasHeight = 1, liquifySettings = DEFAULT_LIQUIFY_SETTINGS) {
  const safeSeed = normaliseSeed(seed);
  const settings = normaliseLiquifySettings(liquifySettings);
  const nx = radius > 0 ? localX / radius : 0;
  const ny = radius > 0 ? localY / radius : 0;
  const rawFalloff = smoothFalloff(1 - (distance / Math.max(1, radius)));
  const falloff = shapeLiquifyFalloff(rawFalloff, settings);
  const dragDistance = Math.hypot(dx, dy);
  const forceScale = getLiquifyForceScale(settings);
  const flowScale = getLiquifyFlowScale(settings);
  const twistScale = getLiquifyTwistScale(settings);
  const chaosScale = getLiquifyChaosScale(settings);
  const detailScale = getLiquifyDetailScale(settings);
  const dragScale = (mode === 'soft_nudge' ? 0.34 : 1) * flowScale;
  const power = strength * falloff * forceScale;
  const worldX = centreX + localX;
  const worldY = centreY + localY;

  if (mode === 'field_paint') {
    const flow = flowField ? sampleFlowField(flowField, worldX, worldY) : { x: 0, y: 0 };
    const turbulence = (fastFbm2(worldX * 0.018 * detailScale, worldY * 0.018 * detailScale, safeSeed + 227) - 0.5) * chaosScale;
    const angle = Math.atan2(flow.y || dy || 0.001, flow.x || dx || 0.001) + (turbulence * 0.9);
    const storedSpeed = Math.hypot(flow.x, flow.y);
    const freshSpeed = dragDistance * 0.32 * flowScale;
    const amount = power * (storedSpeed + freshSpeed);

    return {
      x: Math.cos(angle) * amount,
      y: Math.sin(angle) * amount,
    };
  }

  if (mode === 'clock_well' || mode === 'counter_well') {
    const direction = mode === 'clock_well' ? 1 : -1;
    const angle = direction * power * twistScale * Math.min(1.2, 0.18 + (dragDistance * 0.035));
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    return {
      x: (localX * cos) - (localY * sin) - localX,
      y: (localX * sin) + (localY * cos) - localY,
    };
  }

  if (mode === 'collapse' || mode === 'bloom') {
    const direction = mode === 'collapse' ? -1 : 1;
    const amount = radius * power * 0.42 * direction;

    return {
      x: nx * amount,
      y: ny * amount,
    };
  }

  if (mode === 'shardfield') {
    const scale = 0.018 * detailScale;
    const shard = fbm(worldX * scale, worldY * scale, safeSeed + 11, 3);
    const shardFine = fbm(worldX * scale * 3.1, worldY * scale * 3.1, safeSeed + 29, 2);
    const angleBase = Math.atan2(localY, localX);
    const snapCount = 6 + Math.round(shard * 10);
    const snapped = Math.round((angleBase + ((shardFine - 0.5) * 1.8 * chaosScale)) / ((Math.PI * 2) / snapCount)) * ((Math.PI * 2) / snapCount);
    const fracture = Math.pow(Math.abs((shard * 2) - 1), 0.42);
    const edgePower = smoothFalloff(distance / Math.max(1, radius));
    const amount = dragDistance * power * (0.45 + fracture * 1.45);

    return {
      x: (Math.cos(snapped) * amount) + (dx * power * 0.18 * edgePower),
      y: (Math.sin(snapped) * amount) + (dy * power * 0.18 * edgePower),
    };
  }

  if (mode === 'veinflow') {
    const scale = 0.026 * detailScale;
    const n1 = fbm(worldX * scale, worldY * scale, safeSeed + 71, 3);
    const n2 = fbm((worldX + 91.7) * scale, (worldY - 47.2) * scale, safeSeed + 103, 3);
    const branch = Math.sin((n1 * 18) + (Math.atan2(localY, localX) * 5));
    const vein = Math.pow(1 - Math.abs(branch), 3);
    const angle = Math.atan2(n2 - 0.5, n1 - 0.5) + (branch * 0.8);
    const amount = radius * power * vein * 0.38;

    return {
      x: Math.cos(angle) * amount + dx * power * 0.18,
      y: Math.sin(angle) * amount + dy * power * 0.18,
    };
  }

  if (mode === 'faultline') {
    const angle = Math.atan2(dy || 0.001, dx || 0.001) + Math.PI / 2;
    const lineNoise = fastFbm2(worldX * 0.015 * detailScale, worldY * 0.015 * detailScale, safeSeed + 151);
    const faultLine = (localX * Math.cos(angle)) + (localY * Math.sin(angle)) + ((lineNoise - 0.5) * radius * 0.9);
    const side = faultLine >= 0 ? 1 : -1;
    const crack = Math.exp(-Math.abs(faultLine) / Math.max(1, radius * 0.12));
    const shear = power * (0.28 + crack * 1.8);

    return {
      x: dx * shear * side,
      y: dy * shear * side,
    };
  }

  if (mode === 'gravity_well') {
    const safeDist = Math.max(1, distance);
    const orbit = Math.atan2(localY, localX) + Math.PI / 2;
    const inward = radius * power * 0.34 * (1 - safeDist / Math.max(1, radius));
    const swirl = radius * power * 0.16 * twistScale;

    return {
      x: (-nx * inward) + (Math.cos(orbit) * swirl),
      y: (-ny * inward) + (Math.sin(orbit) * swirl),
    };
  }

  if (mode === 'waveglass') {
    const wave = Math.sin((distance * 0.19 * detailScale) - (dragDistance * 0.7));
    const amount = wave * radius * power * 0.18 * (0.55 + chaosScale * 0.45);

    return {
      x: nx * amount + dx * power * 0.12,
      y: ny * amount + dy * power * 0.12,
    };
  }

  if (mode === 'water_reflection') {
    const perspective = getWaterPerspective(worldY, canvasHeight);

    if (perspective.horizontal <= 0.0001) {
      return { x: 0, y: 0, perspective: 0 };
    }

    const horizontalSign = Math.sign(dx || 1);
    const stretch = 1 + (perspective.horizontal * 2.5);
    const brokenBandNoise = fastFbm2(worldY * (0.045 * detailScale / stretch), (worldX * 0.006 * detailScale) + (safeSeed * 0.011), safeSeed + 311);
    const fineNoise = fastFbm2(worldX * (0.035 + perspective.horizontal * 0.03), worldY * 0.018, safeSeed + 733);
    const bandBreak = fastFbm2(worldY * 0.071, worldX * 0.012, safeSeed + 917);
    const longWave = Math.sin((worldY * 0.055 / stretch) + (safeSeed * 0.133) + ((bandBreak - 0.5) * 2.8));
    const tightWave = Math.sin((worldY * 0.21 * stretch) + (safeSeed * 0.071) + (worldX * 0.014) + ((fineNoise - 0.5) * 2.2));
    const bandDirection = Math.sin(((brokenBandNoise + (longWave * 0.18) + ((bandBreak - 0.5) * 0.22)) * Math.PI * 2)) >= 0 ? 1 : -1;
    const lowerSmearBoost = 0.65 + (perspective.horizontal * 1.55);
    const bandAmount = dragDistance * power * bandDirection * (0.24 + Math.abs((brokenBandNoise - 0.5) * 2) * 0.82) * lowerSmearBoost * perspective.horizontal;
    const rippleAmount = radius * power * (0.02 + perspective.horizontal * 0.04) * (tightWave + (longWave * 0.42) + ((fineNoise - 0.5) * 0.86)) * perspective.horizontal;
    const verticalWobble = radius * power * 0.011 * (((fineNoise - 0.5) * 1.5) + (longWave * 0.35)) * perspective.vertical;
    const edgeBreak = smoothFalloff(distance / Math.max(1, radius)) * radius * power * 0.008 * (fastFbm2(worldX * 0.05, worldY * 0.023, safeSeed + 991) - 0.5) * perspective.horizontal;

    return {
      x: (horizontalSign * (bandAmount + rippleAmount)) + (nx * edgeBreak),
      y: verticalWobble + (ny * edgeBreak * 0.35),
      perspective: perspective.horizontal,
    };
  }

  if (mode === 'rim_shear') {
    const ring = smoothFalloff(1 - Math.abs((distance / Math.max(1, radius)) - (0.58 + settings.edge * 0.32)) / 0.34);

    return {
      x: dx * strength * ring * flowScale * forceScale,
      y: dy * strength * ring * flowScale * forceScale,
    };
  }

  return {
    x: dx * power * dragScale,
    y: dy * power * dragScale,
  };
}

export function drawDistortionStrokeWithinSelection(targetCanvas, reconstructCanvas, previousPoint, point, options, selection = null) {
  if (!targetCanvas || !previousPoint || !point) return false;

  const ctx = targetCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  const mode = normaliseDistortionMode(options?.distortionMode || options?.liquifyMode);
  const radius = Math.max(4, Math.min(440, Number(options?.size) || 40));
  const strength = Math.max(0.02, Math.min(1, Number(options?.intensity ?? options?.strength ?? options?.opacity ?? 0.55)));
  const liquifySettings = normaliseLiquifySettings(options?.liquifySettings || options?.distortionSettings);
  const seed = normaliseSeed(options?.strokeSeed ?? options?.seed ?? 0);
  const dx = point.x - previousPoint.x;
  const dy = point.y - previousPoint.y;
  const dragDistanceSq = (dx * dx) + (dy * dy);
  const dragDistance = Math.sqrt(dragDistanceSq);
  const minUsefulDrag = mode === 'water_reflection' ? 0.35 : Math.max(0.35, Math.min(5, radius * 0.035 * (1.35 - liquifySettings.flow)));

  if (dragDistance < minUsefulDrag && mode !== 'restore') return false;

  const centreX = point.x;
  const centreY = point.y;
  const pad = Math.ceil(radius + dragDistance + 8);
  const startX = Math.max(0, Math.floor(centreX - pad));
  const startY = Math.max(0, Math.floor(centreY - pad));
  const endX = Math.min(targetCanvas.width, Math.ceil(centreX + pad));
  const endY = Math.min(targetCanvas.height, Math.ceil(centreY + pad));
  const width = Math.max(1, endX - startX);
  const height = Math.max(1, endY - startY);

  if (width <= 0 || height <= 0) return false;

  const sourceData = ctx.getImageData(startX, startY, width, height);
  const resultData = copyImageData(sourceData);
  const result = resultData.data;
  const source = sourceData.data;

  let reconstructData = null;
  if (mode === 'restore' && reconstructCanvas) {
    const reconstructCtx = reconstructCanvas.getContext('2d', { willReadFrequently: true });
    if (reconstructCtx) {
      reconstructData = reconstructCtx.getImageData(startX, startY, width, height).data;
    }
  }

  let maskData = null;
  if (selection) {
    maskData = getSelectionMaskData(width, height, selection, startX, startY);
  }


  if (mode === 'water_reflection') {
    const waterChanged = drawWaterReflectionStroke(
      source,
      result,
      width,
      height,
      startX,
      startY,
      centreX,
      centreY,
      dx,
      dy,
      Math.min(radius, WATER_REFLECTION_MAX_RADIUS),
      strength * getLiquifyForceScale(liquifySettings),
      seed,
      targetCanvas.height,
      maskData,
      liquifySettings,
    );

    if (!waterChanged) return false;

    ctx.putImageData(resultData, startX, startY);
    return true;
  }

  const flowField = mode === 'field_paint' ? getFlowField(targetCanvas) : null;
  const radiusSq = radius * radius;
  const invRadius = 1 / Math.max(1, radius);

  let changed = false;

  for (let y = 0; y < height; y += 1) {
    const canvasY = startY + y;

    for (let x = 0; x < width; x += 1) {
      if (!selectionContains(maskData, width, x, y)) {
        continue;
      }

      const canvasX = startX + x;
      const localX = canvasX - centreX;
      const localY = canvasY - centreY;
      const distanceSq = (localX * localX) + (localY * localY);

      if (distanceSq > radiusSq) {
        continue;
      }

      const distance = Math.sqrt(distanceSq);
      const falloff = smoothFalloff(1 - (distance * invRadius));
      const dstIndex = (y * width + x) * 4;

      if (mode === 'restore' && reconstructData) {
        const mix = strength * falloff;
        const keep = 1 - mix;

        result[dstIndex] = result[dstIndex] * keep + reconstructData[dstIndex] * mix;
        result[dstIndex + 1] = result[dstIndex + 1] * keep + reconstructData[dstIndex + 1] * mix;
        result[dstIndex + 2] = result[dstIndex + 2] * keep + reconstructData[dstIndex + 2] * mix;
        result[dstIndex + 3] = result[dstIndex + 3] * keep + reconstructData[dstIndex + 3] * mix;
        changed = true;
        continue;
      }

      if (mode === 'field_paint' && flowField) {
        addToFlowField(flowField, canvasX, canvasY, dx, dy, falloff, strength);
      }

      const offset = getSourceOffsetForMode(
        mode,
        localX,
        localY,
        dx,
        dy,
        radius,
        strength,
        distance,
        centreX,
        centreY,
        flowField,
        seed,
        targetCanvas.height,
        liquifySettings,
      );

      const sampleX = x - offset.x;
      const sampleY = y - offset.y;

      if (sampleX < 0 || sampleY < 0 || sampleX >= width || sampleY >= height) {
        continue;
      }

      sampleBilinearInto(source, width, height, sampleX, sampleY, result, dstIndex);
      changed = true;
    }
  }

  if (!changed) return false;

  ctx.putImageData(resultData, startX, startY);
  return true;
}

export function drawLiquifyStrokeWithinSelection(targetCanvas, reconstructCanvas, previousPoint, point, options, selection = null) {
  return drawDistortionStrokeWithinSelection(targetCanvas, reconstructCanvas, previousPoint, point, options, selection);
}